import { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap } from 'react-leaflet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

function MapUpdater({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, 13);
  }, [center, map]);
  return null;
}

export const LocationSelector = ({ location, onChange, showRadius = true, showMap = true }) => {
  const [address, setAddress] = useState(location?.address || '');
  const [radius, setRadius] = useState(location?.radius || 500);
  const [coords, setCoords] = useState(location?.coords || [43.5808, 7.1251]);
  const [suggestions, setSuggestions] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isMapVisible, setIsMapVisible] = useState(true);

  useEffect(() => {
    onChange({ address, coords, radius });
  }, [address, coords, radius]);

  const expandAbbreviations = (text) => {
    const abbreviations = {
      'av': 'avenue',
      'bd': 'boulevard',
      'mal': 'maréchal',
      'ch': 'chemin',
      'che': 'chemin',
      'pl': 'place',
      'sq': 'square',
      'imp': 'impasse',
      'all': 'allée',
      'rte': 'route',
      'crs': 'cours',
      'fg': 'faubourg',
      'pass': 'passage',
      'r': 'rue'
    };
    
    let expanded = text.toLowerCase();
    
    Object.entries(abbreviations).forEach(([abbr, full]) => {
      const regex = new RegExp(`\\b${abbr}\\b`, 'gi');
      expanded = expanded.replace(regex, full);
    });
    
    return expanded;
  };

  const searchAddress = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      return;
    }

    setIsSearching(true);
    
    if (window.searchTimeout) clearTimeout(window.searchTimeout);
    
    window.searchTimeout = setTimeout(async () => {
      try {
        const expandedQuery = expandAbbreviations(query);
        
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?` +
          `q=${encodeURIComponent(expandedQuery)}&` +
          `format=json&` +
          `limit=10&` +
          `countrycodes=fr&` +
          `addressdetails=1`
        );
        const data = await response.json();
        
        const filtered = data.filter(item => 
          item.address?.postcode?.startsWith('06')
        );
        
        const simplified = filtered.map(item => ({
          ...item,
          simple_address: formatSimpleAddress(item.address)
        }));
        
        setSuggestions(simplified.slice(0, 6));
      } catch (error) {
        console.error(error);
        setSuggestions([]);
      } finally {
        setIsSearching(false);
      }
    }, 500);
  };

  const formatSimpleAddress = (address) => {
    const parts = [];
    
    if (address.house_number) parts.push(address.house_number);
    if (address.road) parts.push(address.road);
    else if (address.street) parts.push(address.street);
    else if (address.hamlet) parts.push(address.hamlet);
    
    const street = parts.join(' ');
    const city = address.city || address.town || address.village || address.municipality || '';
    const postcode = address.postcode || '';
    
    if (street && city) {
      return `${street}, ${postcode} ${city}`;
    } else if (city) {
      return `${postcode} ${city}`;
    }
    return address.display_name;
  };

  const selectSuggestion = (suggestion) => {
    setAddress(suggestion.simple_address);
    setCoords([parseFloat(suggestion.lat), parseFloat(suggestion.lon)]);
    setSuggestions([]);
    setIsMapVisible(true);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label>Adresse *</Label>
        <div className="relative">
          <Input
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              searchAddress(e.target.value);
              if (e.target.value.length > 0) setIsMapVisible(false);
            }}
            onFocus={() => setIsMapVisible(false)}
            placeholder="12 rue de la République, Antibes"
          />
          {isSearching && (
            <div className="absolute right-3 top-3">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
            </div>
          )}
        </div>
        
        {suggestions.length > 0 && (
          <div className="mt-2 bg-white border-2 border-primary rounded-lg shadow-2xl max-h-64 overflow-y-auto">
            <div className="p-2 bg-primary/5 border-b">
              <p className="text-xs text-muted-foreground">
                {suggestions.length} résultat{suggestions.length > 1 ? 's' : ''} trouvé{suggestions.length > 1 ? 's' : ''}
              </p>
            </div>
            {suggestions.map((sug, i) => (
              <button
                key={i}
                type="button"
                onClick={() => selectSuggestion(sug)}
                className="w-full p-4 hover:bg-muted/50 text-left border-b last:border-b-0 transition-colors"
              >
                <p className="font-heading text-sm font-semibold text-foreground">
                  {sug.simple_address}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>

      {showRadius && (
        <div>
          <Label>Dans un rayon de {radius >= 1000 ? `${(radius/1000).toFixed(1)} km` : `${radius} m`}</Label>
          <input
            type="range"
            min="100"
            max="1000"
            step="50"
            value={radius}
            onChange={(e) => setRadius(parseInt(e.target.value))}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
          />
          <div className="flex justify-between text-xs text-gray-500 mt-1">
            <span>100 m</span>
            <span>1 km</span>
          </div>
        </div>
      )}

      {showMap && isMapVisible && (
        <div className="h-64 rounded-lg overflow-hidden border border-gray-200">
          <MapContainer center={coords} zoom={13} style={{ height: '100%', width: '100%' }}>
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            <Marker position={coords} />
            {showRadius && (
              <Circle
                center={coords}
                radius={radius}
                pathOptions={{ color: 'blue', fillColor: 'blue', fillOpacity: 0.1 }}
              />
            )}
            <MapUpdater center={coords} />
          </MapContainer>
        </div>
      )}
    </div>
  );
};
